/** @type {import('tailwindcss').Config} */
module.exports = {
  darkMode: ['class'],
  content: [
    './src/**/*.{js,ts,jsx,tsx,mdx}',
    './shared/**/*.{js,ts,jsx,tsx,mdx}',
  ],
  theme: {
    extend: {
      fontSize: {
        '2xs': '0.625rem', // 10px at browser default font size of 16px
      },
      fontFamily: {
        heading: ['Replica', 'system-ui', 'sans-serif'],
      },
      borderRadius: {
        lg: 'var(--radius)',
        md: 'calc(var(--radius) - 2px)',
        sm: 'calc(var(--radius) - 4px)',
      },
      colors: {
        // Override base tailwind colors with Norm's palette
        // Gray
        gray: {
          50: '#F0F0F3',
          100: '#E5E6EA',
          200: '#DADCE1',
          300: '#BABDC6',
          400: '#A5A8B5',
          500: '#8F93A3',
          600: '#5E6272',
          700: '#32343C',
          800: '#292A32',
          900: '#292A32',
        },
        // Red
        red: {
          50: '#FFEEEB',
          100: '#FFDED7',
          200: '#FFBFB0',
          300: '#FF9F89',
          400: '#FF4F26',
          500: '#FE2F00',
          600: '#D72800',
          700: '#B02100',
          800: '#891900',
          900: '#611200',
        },
        // Yellow
        yellow: {
          50: '#FFF9EB',
          100: '#FFF3D7',
          200: '#FFE6B0',
          300: '#FFDA89',
          400: '#FFC84E',
          500: '#FEAF00',
          600: '#D79400',
          700: '#975A16',
          800: '#895E00',
          900: '#4E3500',
        },
        // Green
        green: {
          50: '#EBFFF4',
          100: '#D7FFE8',
          200: '#B0FFD2',
          300: '#9CFFC7',
          400: '#00FE6F',
          500: '#00D75E',
          600: '#00B04D',
          700: '#00893C',
          800: '#00612B',
          900: '#003A19',
        },
        // Blue
        blue: {
          50: '#EEEBFF',
          100: '#CEC3FF',
          200: '#AF9CFF',
          300: '#6F4EFF',
          400: '#3F13FF',
          500: '#2800D7',
          600: '#2100B0',
          700: '#190089',
          800: '#120061',
          900: '#0B003A',
        },
        // Orange
        orange: {
          50: '#FFFAF0',
          100: '#FEEBCB',
          200: '#FBD38D',
          300: '#F6AD55',
          400: '#ED8936',
          500: '#DD6B20',
          600: '#C05621',
          700: '#9C4221',
          800: '#7B341E',
          900: '#652B19',
        },
        // Teal
        teal: {
          50: '#DFFCF6',
          100: '#A9F7E8',
          200: '#85F4DE',
          300: '#61F0D5',
          400: '#19EAC2',
          500: '#13C9A6',
          600: '#11B797',
          700: '#0E937A',
          800: '#074C3E',
          900: '#042821',
        },
        // Purple
        purple: {
          50: '#F9EBFF',
          100: '#F3D7FF',
          200: '#E6B0FF',
          300: '#D475FF',
          400: '#C23AFF',
          500: '#AF00FE',
          600: '#9400D7',
          700: '#7900B0',
          800: '#5E0089',
          900: '#430061',
        },

        // Map amber to yellow for compatibility
        amber: {
          50: '#FFF9EB',
          100: '#FFF3D7',
          200: '#FFE6B0',
          300: '#FFDA89',
          400: '#FFC84E',
          500: '#FEAF00',
          600: '#D79400',
          700: '#975A16',
          800: '#895E00',
          900: '#4E3500',
        },

        // ShadCN theme colors (keep these for compatibility)
        background: 'hsl(var(--background))',
        foreground: 'hsl(var(--foreground))',
        card: {
          DEFAULT: 'hsl(var(--card))',
          foreground: 'hsl(var(--card-foreground))',
        },
        popover: {
          DEFAULT: 'hsl(var(--popover))',
          foreground: 'hsl(var(--popover-foreground))',
        },
        primary: {
          DEFAULT: 'hsl(var(--primary))',
          foreground: 'hsl(var(--primary-foreground))',
        },
        secondary: {
          DEFAULT: 'hsl(var(--secondary))',
          foreground: 'hsl(var(--secondary-foreground))',
        },
        muted: {
          DEFAULT: 'hsl(var(--muted))',
          foreground: 'hsl(var(--muted-foreground))',
        },
        accent: {
          DEFAULT: 'hsl(var(--accent))',
          foreground: 'hsl(var(--accent-foreground))',
        },
        destructive: {
          DEFAULT: 'hsl(var(--destructive))',
          foreground: 'hsl(var(--destructive-foreground))',
        },
        border: 'hsl(var(--border))',
        input: 'hsl(var(--input))',
        ring: 'hsl(var(--ring))',
        chart: {
          1: 'hsl(var(--chart-1))',
          2: 'hsl(var(--chart-2))',
          3: 'hsl(var(--chart-3))',
          4: 'hsl(var(--chart-4))',
          5: 'hsl(var(--chart-5))',
        },
        sidebar: {
          DEFAULT: 'hsl(var(--sidebar-background))',
          foreground: 'hsl(var(--sidebar-foreground))',
          primary: 'hsl(var(--sidebar-primary))',
          'primary-foreground': 'hsl(var(--sidebar-primary-foreground))',
          accent: 'hsl(var(--sidebar-accent))',
          'accent-foreground': 'hsl(var(--sidebar-accent-foreground))',
          border: 'hsl(var(--sidebar-border))',
          ring: 'hsl(var(--sidebar-ring))',
        },

        // Additional Chakra-specific named colors
        black: '#000000',
        normBlack: '#17181C',
        normWhite: '#FBFBFB',
      },
      keyframes: {
        'accordion-down': {
          from: {
            height: '0',
          },
          to: {
            height: 'var(--radix-accordion-content-height)',
          },
        },
        'accordion-up': {
          from: {
            height: 'var(--radix-accordion-content-height)',
          },
          to: {
            height: '0',
          },
        },
        'slide-in': {
          from: {
            opacity: '0',
            transform: 'translateY(-6px)',
          },
          to: {
            opacity: '1',
            transform: 'translateY(0)',
          },
        },
      },
      animation: {
        'accordion-down': 'accordion-down 0.2s ease-out',
        'accordion-up': 'accordion-up 0.2s ease-out',
        'slide-in': 'slide-in 0.4s ease-out',
      },
    },
  },
  plugins: [require('tailwindcss-animate')],
  safelist: ['webviewer'],
};
